import React, { useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';

function safeUrl(url: string): string {
  const trimmed = url.trim().toLowerCase();
  if (trimmed.startsWith('javascript:') || trimmed.startsWith('data:')) return '';
  return url;
}

export interface ForecastMarkdownProps {
  source: string;
  isDark: boolean;
  /** Smaller body text (e.g. daily cards) */
  compact?: boolean;
  className?: string;
  style?: React.CSSProperties;
}

/**
 * Renders forecast prose as GitHub-flavored Markdown (bold, lists, links, etc.).
 * Uses remark-breaks so single newlines behave like the previous pre-wrap plain text.
 */
export const ForecastMarkdown: React.FC<ForecastMarkdownProps> = ({
  source,
  isDark,
  compact = false,
  className,
  style,
}) => {
  const color = isDark ? '#d9d9d9' : '#595959';
  const linkColor = isDark ? '#69b1ff' : '#1677ff';
  const codeBg = isDark ? '#2a2a2a' : 'rgba(0, 0, 0, 0.06)';

  const fontSize = compact ? 12 : 14;

  const components = useMemo(
    () => ({
      p: ({ children }: { children?: React.ReactNode }) => (
        <p style={{ margin: '0 0 0.5em', lineHeight: 1.5, color, fontSize }}>{children}</p>
      ),
      strong: ({ children }: { children?: React.ReactNode }) => (
        <strong style={{ color: isDark ? '#f0f0f0' : '#262626' }}>{children}</strong>
      ),
      em: ({ children }: { children?: React.ReactNode }) => <em style={{ color }}>{children}</em>,
      ul: ({ children }: { children?: React.ReactNode }) => (
        <ul style={{ margin: '0 0 0.5em', paddingLeft: 20, color, fontSize }}>{children}</ul>
      ),
      ol: ({ children }: { children?: React.ReactNode }) => (
        <ol style={{ margin: '0 0 0.5em', paddingLeft: 20, color, fontSize }}>{children}</ol>
      ),
      li: ({ children }: { children?: React.ReactNode }) => (
        <li style={{ marginBottom: 4 }}>{children}</li>
      ),
      a: ({ href, children }: { href?: string; children?: React.ReactNode }) => (
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: linkColor, wordBreak: 'break-word' }}
        >
          {children}
        </a>
      ),
      code: ({ className: cn, children, ...props }: React.ComponentPropsWithoutRef<'code'>) => {
        const match = /language-(\w+)/.exec(cn || '');
        const isInline = !match;
        if (!isInline) {
          return (
            <pre
              style={{
                margin: '0 0 0.5em',
                padding: 8,
                borderRadius: 4,
                overflow: 'auto',
                backgroundColor: codeBg,
                fontSize: compact ? 11 : 12,
              }}
            >
              <code className={cn} style={{ color }} {...props}>
                {children}
              </code>
            </pre>
          );
        }
        return (
          <code
            style={{
              padding: '1px 4px',
              borderRadius: 2,
              backgroundColor: codeBg,
              fontSize: compact ? 11 : 12,
              color,
            }}
            {...props}
          >
            {children}
          </code>
        );
      },
      blockquote: ({ children }: { children?: React.ReactNode }) => (
        <blockquote
          style={{
            margin: '0 0 0.5em',
            paddingLeft: 12,
            borderLeft: `3px solid ${isDark ? '#434343' : '#d9d9d9'}`,
            color,
            fontSize,
          }}
        >
          {children}
        </blockquote>
      ),
    }),
    [color, linkColor, codeBg, compact, fontSize, isDark]
  );

  return (
    <div
      className={className}
      style={{
        wordBreak: 'break-word',
        maxWidth: '100%',
        ...style,
      }}
    >
      <ReactMarkdown remarkPlugins={[remarkGfm, remarkBreaks]} urlTransform={safeUrl} components={components}>
        {source}
      </ReactMarkdown>
    </div>
  );
};
